import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Typography,
  useTheme,
  alpha,
  Switch,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Help as HelpIcon,
  ExpandLess,
  ExpandMore,
  KeyboardArrowUp as ArrowUpIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface SidebarFooterProps {
  toggleTheme?: () => void;
  isDarkMode?: boolean;
}

const SidebarFooter: React.FC<SidebarFooterProps> = ({ toggleTheme, isDarkMode = true }) => {
  const theme = useTheme();
  const { user, logout } = useAuth();
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<null | HTMLElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Debug: Log user data to see what we're receiving
  React.useEffect(() => {
    console.log('SidebarFooter - User data:', user);
    
    // If we have a user but missing email/name, try to fetch complete profile
    if (user && user.id && !user.email && !user.fullName) {
      console.log('User data incomplete, attempting to fetch full profile...');
      // We can't import useAuthStore hook here, so we'll use the context's refreshAuth instead
      // This will be handled by the SyncSession component anyway
    }
  }, [user]);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleSettingsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchor(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await logout();
  };

  return (
    <>
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: alpha(theme.palette.background.paper, 0.4),
        }}
      >
        {/* Collapse Toggle Header */}
        <Box sx={{ px: 1, py: 0.5 }}>
          <ListItemButton
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              borderRadius: 1.5,
              minHeight: 44,
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.08),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Avatar 
                sx={{ 
                  width: 28, 
                  height: 28,
                  fontSize: '0.875rem',
                  backgroundColor: theme.palette.primary.main,
                }}
              >
                {user?.username?.[0]?.toUpperCase() || 
                 user?.email?.[0]?.toUpperCase() || 
                 user?.fullName?.[0]?.toUpperCase() || 
                 'A'}
              </Avatar>
            </ListItemIcon>
            <ListItemText 
              primary={user?.fullName || user?.username || user?.email || 'Your Account'}
              secondary={!isExpanded ? (user?.email || 'Signed In') : undefined}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
              secondaryTypographyProps={{
                fontSize: '0.75rem',
                sx: {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }
              }}
            />
            <IconButton
              size="small"
              sx={{ 
                ml: 1,
                color: theme.palette.text.secondary,
              }}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </ListItemButton>
        </Box>

        {/* Collapsible Content */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List sx={{ py: 0, pb: 1 }}>
            {/* Settings Button */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleSettingsMenuOpen}
                sx={{
                  mx: 1,
                  borderRadius: 1.5,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.action.hover, 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Settings" 
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                  }}
                />
              </ListItemButton>
            </ListItem>

            {/* User Profile Button */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleUserMenuOpen}
                sx={{
                  mx: 1,
                  borderRadius: 1.5,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.action.hover, 0.08),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary="Profile & Account"
                  secondary={user?.email || `ID: ${user?.id?.substring(0, 8)}...` || 'Signed In'}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.75rem',
                    sx: {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Collapse>
      </Box>

      {/* Settings Menu */}
      <Menu
        anchorEl={settingsMenuAnchor}
        open={Boolean(settingsMenuAnchor)}
        onClose={handleSettingsMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 240,
            ml: 1,
          }
        }}
      >
        <MenuItem>
          <ListItemIcon>
            {isDarkMode ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText primary="Dark Mode" />
          <Switch
            edge="end"
            checked={isDarkMode}
            onChange={toggleTheme}
            size="small"
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => window.open('https://docs.anthropic.com/en/docs/claude-code', '_blank')}>
          <ListItemIcon>
            <HelpIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Help & Documentation" />
        </MenuItem>
      </Menu>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 240,
            ml: 1,
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {user?.fullName || user?.username || user?.email || 'Your Account'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email || (user?.id ? `ID: ${user.id.substring(0, 8)}...` : 'Signed In')}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleUserMenuClose}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Profile" />
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sign out" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default SidebarFooter;